import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { storage } from '@uhg-abyss/web/tools/storage';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { Toggle } from '@uhg-abyss/web/ui/Toggle';
import React, { useContext, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { MOCK_GUEST_DATA } from '../../../common/MockMemberInfo';
import { PortalIdentifier } from '../../../common/Portal';
import { reciprocityIdMapping } from '../../../common/Portal/mockMedicalGuestPortalMemberInfo';
import { useMockReciprocity } from '../../../context/MockReciprocityContext';
import { PortalContext } from '../../../context/PortalContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useOBAPI } from '../../../hooks/useOBAPI';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { modifyRulesPackageKey } from '../../../utils/user.utils';
import { Constants, LOB, PortalSource } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import {
  dateFormatUTC,
  dateGroupByYearMonthDay,
} from '../../Utils/datesUtils/formatDate';
import {
  findLoggedInMember,
  isMaestroMNRMember,
  isNonMaestroMNRMember,
} from '../../Utils/memberUtils/memberUtils';
import { StyledSelectInput } from '../DemoHeader/DemoHeader.styled';
import { MemberInfo, MemberProfile } from './memberProfile';

const { ACTIVE_MEMBER_INDEX, DEMO_MEMBER_INFO, LOGGED_IN_MEMBER_INDEX } =
  Constants.STORAGE_KEYS.SESSION;

const StyledTextInput = styled(TextInput, {
  '.abyss-text-input-label': {
    color: '$core.color.brand.100',
  },
});

const StyledTextInputArea = styled(TextInputArea, {
  width: '100%',
  '.abyss-text-input-area-label': {
    color: '$core.color.brand.100',
  },

  '&::placeholder': {
    color: '#929496',
  },
});

const MockReciprocitySection = styled('div', {
  borderTop: '1px solid $core.color.neutral.30',
  borderBottom: '1px solid $core.color.neutral.30',
  padding: '12px 4px',
  marginTop: '12px',
  marginBottom: '12px',
  backgroundColor: '#FAFAFA',
});

const MockReciprocitySectionTitle = styled('div', {
  color: '$core.color.brand.100',
  fontWeight: 'bold',
  marginBottom: '8px',
  fontSize: '14px',
});

const convertToBase64String = (data: Record<string, string>): string => {
  const jsonString = JSON.stringify(data);
  const base64String = Buffer.from(jsonString, 'utf-8').toString('base64');
  return base64String;
};

export interface BigNVars {
  firstName?: string;
  lastName?: string;
  dob?: string;
  memberId?: string;
  groupNumber?: string;
  delsysCode?: string;
  id?: string;
  userName?: string;
  portalSource?: string;
  dateOfBirth?: string;
}

type Props = {
  setMemberProfiles: (a: MemberInfo[]) => void;
  setMemberId?: (a: number) => void;
  isGuest?: boolean;
};

export const setProfileData = (
  result: { data: MemberProfile },
  setMemberProfiles: (a: MemberInfo[]) => void,
  setMemberState: (a: MemberInfo[]) => void,
  firstName: string,
  lastName: string,
  groupNumber: string,
  memberId: string,
  dob: string,
  id: string,
  userName: string,
  portalSource: string,
  lob: LOB,
  usePreProd: boolean,
  setMemberId: (x: string | number) => void
) => {
  if (result?.data?.memberInfo) {
    const sessionStoragePortalSource =
      storage.session.get(Constants.STORAGE_KEYS.SESSION.PORTAL_SOURCE) || {};
    const loggedInMemberIndex = findLoggedInMember(result?.data?.memberInfo, {
      FirstName: firstName,
      LastName: lastName,
      DOB: dob,
      GroupNumber: groupNumber,
      MemberNumber: memberId,
      id,
      userName,
      portalSource,
    });
    setMemberState(result?.data?.memberInfo);
    storage.session.set(ACTIVE_MEMBER_INDEX, loggedInMemberIndex.toString());
    storage.session.set(LOGGED_IN_MEMBER_INDEX, loggedInMemberIndex);
    setMemberProfiles(
      result?.data?.memberInfo?.filter(
        (member) =>
          !!member.eligibility[0]?.memberHealthCoverage?.coverageType?.length
      )
    );
    storage.session.set(DEMO_MEMBER_INFO, {
      ...((lob === LOB.ENI ||
        lob === LOB.CNS ||
        lob === LOB.IFP ||
        (lob === LOB.MNR &&
          sessionStoragePortalSource === PortalSource.MAESTRO)) && {
        firstName,
        lastName,
        groupNumber,
        memberId,
        dob,
      }),
      ...(lob === LOB.MNR &&
        sessionStoragePortalSource !== PortalSource.MAESTRO && {
          id,
          userName,
          portalSource,
        }),
      lob,
      usePreProd,
    });
    setMemberId(memberId);
  }
};

export const ObapiDemo = ({
  setMemberProfiles,
  setMemberId = () => {},
  isGuest = false,
}: Props) => {
  const form = useForm();
  const suppressionLagoonData = useLagoon('feature-supression')();
  const includeLagoonData = useLagoon('feature-inclusion')();
  const tierBenefits = useLagoon('tier-benefit')();
  const setUIFeatureSuppressionState = useStore(
    StoreKeys.SET_UI_FEATURE_SUPPRESSION
  );
  const setUIFeatureInclusionState = useStore(
    StoreKeys.SET_UI_FEATURE_INCLUSION
  );

  const [
    requestReciprocityId,
    enableMnrCosmos,
    showIsPreEffectiveIndicator,
    extractProductCodeMarketTypeForUspNonOx,
    policyFilterToggle,
    enableCns,
    useNewReferralsIndicator,
    enableGroupNumberBasedRouting,
    enableMemberInfoCache,
    enableVisionForNonUhcMNR,
    enableVirtualCareEpic,
    enableNonGatedPcpSuppression,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.REQUEST_RECIPROCITY_ID,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MNR_COSMOS,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_ISPREEFFECTIVE_INDICATOR,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_EXTRACT_PRODUCT_CODE_MARKET_TYPE_FOR_USP_NON_OXFORD,
    ConstantsLagoon.FEATURE_FLAGS.MEMBER_INFO_POLICY_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CNS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_REFERRALS_INDICATOR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_GROUP_NUMBER_BASED_ROUTING,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MEMBER_INFO_CACHE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VISION_FOR_NON_UHC_MNR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_GATED_PCP_SUPPRESSION,
  ]);
  const allConfigs = useLagoon('config')();
  const hbpbCodes =
    allConfigs
      ?.find((row) => row.key === 'VISION_NON_UHC_ICARE')
      ?.value?.split(',') || [];

  const { USE_PRE_PROD, IS_PREPROD_USER, USE_OBAPI, ENABLE_PCP, MEMBER_ID } =
    Constants.STORAGE_KEYS.SESSION;

  const [enablePreProd, setEnablePreProd] = useState(
    storage.session.get(IS_PREPROD_USER, false)
  );
  const [useObapi, setUseObapi] = useState(
    storage.session.get(USE_OBAPI, false)
  );
  const [enablePcp, setEnablePcp] = useState(
    storage.session.get(ENABLE_PCP, false)
  );

  enablePreProd === null ? setEnablePreProd(() => false) : '';
  useObapi === null ? setUseObapi(() => false) : '';
  enablePcp === null ? setEnablePcp(() => false) : '';

  const { portalData } = useContext(PortalContext);

  useEffect(() => {
    storage.session.set(USE_PRE_PROD, enablePreProd);
  }, [enablePreProd]);

  const fpcPsxRedirection = useLagoon(
    Constants.LAGOON_TABLE.FPC_PSX_REDIRECTION
  )();

  const eligiblePolicyIds =
    fpcPsxRedirection[0]?.eligiblePolicyNumbers?.split(',') || [];

  const eligibleGroupIds = fpcPsxRedirection[0]?.groupNumber?.split(',') || [];

  const {
    firstName,
    lastName,
    groupNumber,
    memberId,
    dob,
    id,
    userName,
    portalSource,
    lob,
    delsysCode,
    exchangeId,
    ndbLob,
    qhpId,
    coverageEffectiveDate,
    zipCode,
    stateCode,
    language,
    coverageType,
    bigNJsonString,
  } = form.watch();

  const [guestCoverageType, setCoverageType] = useState('M');
  const [populationOptions, setPopulationOptions] = useState(
    Object.keys(reciprocityIdMapping['ENI'])
  );
  const [population, setPopulation] = useState(populationOptions[0] || '');
  const [reciprocityIdOptions, setReciprociryIdOptions] = useState<string[]>(
    []
  );
  const [reciprocityId, setReciprocityId] = useState('');
  const [situsState, setSitusState] = useState('');
  const [membershipCategory, setMembershipCategory] = useState('');
  const [membershipCategoryOptions, setMembershipCategoryOptions] = useState<
    string[]
  >([]);

  const {
    config: mockReciprocityConfig,
    setSelectedCoverageType,
    setMockedReciprocityId,
    setIsEnabled: setMockReciprocityIsEnabled,
  } = useMockReciprocity();

  const setMemberState = useStore(StoreKeys.SET_OBAPI_MEMBERS);
  // const isUserMNRCosmosMember = enableMnrCosmos && isMnrMember(lob);

  const sessionStoragePortalSource =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.PORTAL_SOURCE) || {};

  const isUserMNRMaestroMember = isMaestroMNRMember(
    lob,
    sessionStoragePortalSource
  );
  const isUserNonMaestroMNRMember = isNonMaestroMNRMember(
    lob,
    sessionStoragePortalSource
  );

  const [, getUserProfile] = useOBAPI({
    onCompleted: (result: { data: MemberProfile }) => {
      const memberInfo: MemberInfo[] = modifyRulesPackageKey(
        result?.data?.memberInfo,
        tierBenefits
      );

      const {
        demographics,
        policyNumber,
        lineOfBusiness: memberLob,
      } = memberInfo?.[0] || {};
      const { dateOfBirth = '', name } = demographics;
      const { firstName, lastName } = name;
      const { memberId, id, userName, portalSource } = form.getValues();
      storage.cookie.set(Constants.LOBTARGET, memberLob);

      const mnrMemberKey =
        result?.data?.memberInfo?.[0]?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.[0]?.coverageID.filter(
          (subcId) => subcId?.sourceSysCode === Constants.MEMBER_KEY
        );
      const mnrMemberId = mnrMemberKey?.[0]?.id;

      // setting lob in session storage when lob is changed from demoheader - to pick correct lagoon instance on LOWER ENVIRONMENTS. For higher environments, it will be picked from cookie LOB-Target which is set when user logs in via uhc.
      setLob(memberLob);
      storage.session.set(DEMO_MEMBER_INFO, {});
      setProfileData(
        { data: { memberInfo } },
        setMemberProfiles,
        setMemberState,
        firstName,
        lastName,
        policyNumber || groupNumber, // Keep in this order to prevent demo form from breaking on refresh
        isUserNonMaestroMNRMember ? mnrMemberId : memberId,
        dateGroupByYearMonthDay(dateOfBirth),
        id,
        userName,
        portalSource,
        memberLob,
        enablePreProd,
        setMemberId
      );

      setUIFeatureSuppressionState({
        suppressionLagoonData,
        memberPolicy: groupNumber,
      });
      setUIFeatureInclusionState({
        includeLagoonData,
        memberPolicy: groupNumber,
      });
    },
    onError: () => {},
  });

  const getBigN = () => {
    let bigNVars: BigNVars;

    if (bigNJsonString) {
      bigNVars = parseBigNJson();

      if (!bigNVars) {
        return undefined;
      }
      form.setValue('firstName', bigNVars.firstName);
      form.setValue('lastName', bigNVars.lastName);
      form.setValue('dob', dateFormatUTC(bigNVars.dob));
      form.setValue('groupNumber', bigNVars.groupNumber);
      form.setValue('memberId', bigNVars.memberId);
      form.setValue('id', bigNVars.id);
      form.setValue('userName', bigNVars.userName);
      form.setValue('portalSource', bigNVars.portalSource);
    } else {
      bigNVars = {
        ...((lob === LOB.ENI || lob === LOB.CNS || lob === LOB.IFP) && {
          firstName,
          lastName,
          groupNumber,
          memberId,
          dob: dateGroupByYearMonthDay(dob),
          delsysCode,
        }),
        ...(isUserMNRMaestroMember && {
          firstName,
          lastName,
          memberId,
          dob: dateGroupByYearMonthDay(dob),
        }),
        ...(isUserNonMaestroMNRMember && {
          id,
          userName,
          portalSource,
        }),
      };
    }

    return bigNVars;
  };
  const handleUmrOnClick = () => {
    const bigNVars = getBigN();
    if (!bigNVars) {
      return;
    }

    let basePath = '/umr';
    if (
      portalData.portalIdentifier === PortalIdentifier.GEHA ||
      portalData.portalIdentifier === PortalIdentifier.GEHA_V2
    ) {
      basePath = '/geha';
    } else if (portalData.portalIdentifier === PortalIdentifier.NV) {
      basePath = '/umrNevada';
    }
    window.location.href = `${basePath}?firstName=${bigNVars.firstName}&lastName=${bigNVars.lastName}\
&dob=${bigNVars.dob}&policyId=${bigNVars.groupNumber}&memberId=${bigNVars.memberId}\
&delsysCode=${bigNVars.delsysCode}&useOBAPI=${useObapi}&enablePreProd=${enablePreProd}\
&enablePcp=${enablePcp}`;
    storage.session.set(MEMBER_ID, memberId);
  };

  const handleIfpEarlyPayOnClick = () => {
    const data = {
      ...MOCK_GUEST_DATA,
      exchangeId,
      ndbLob,
      qhpId,
      coverageEffectiveDate,
      zipCode,
      stateCode,
      language,
      coverageType,
      portalSource,
    };
    const lastSegment = convertToBase64String(data);
    window.location.href = `guest/${lastSegment}`;
  };

  const handleObapiOnClick = () => {
    const bigNVars = getBigN();
    if (!bigNVars) {
      return;
    }
    getUserProfile({
      variables: {
        ...bigNVars,
        lob,
        requestReciprocityId,
        eligiblePolicyIds,
        eligibleGroupIds,
        enableGroupNumberBasedRouting,
        usePreProd: enablePreProd,
        enableMnrCosmos,
        showIsPreEffectiveIndicator,
        extractProductCodeMarketTypeForUspNonOx,
        policyFilterToggle,
        enableCns,
        useNewReferralsIndicator,
        enableMemberInfoCache,
        enableVisionForNonUhcMNR,
        enableVirtualCareEligibilities: enableVirtualCareEpic,
        visionNonUhcHPBPCodes: hbpbCodes,
        enableNonGatedPcpSuppression,
      },
    });

    storage.session.set(IS_PREPROD_USER, enablePreProd);
    storage.session.set(MEMBER_ID, memberId);
  };

  const parseGuestLOB = (lobStr: string) =>
    lobStr.replace('-guest', '').replace(/&/g, 'n').toLowerCase();

  const handleGuestOnClick = () => {
    // url query with lob, population, membershipCategory, coverageType, reciprocityId
    const queryParams = new URLSearchParams();
    const parsedLob = parseGuestLOB(lob as string);
    queryParams.append('lob', parsedLob);
    if (population && population !== '') {
      queryParams.append('population', population);
    }
    if (
      membershipCategory &&
      membershipCategory !== '' &&
      membershipCategory !== 'NON_OXFORD'
    ) {
      queryParams.append('membershipCategory', membershipCategory);
    }
    if (guestCoverageType && guestCoverageType !== '') {
      queryParams.append('coverageType', guestCoverageType);
      if (guestCoverageType === 'V') {
        queryParams.append('chipValue', 'Vision');
      }
    }
    if (reciprocityId && reciprocityId !== '') {
      queryParams.append('reciprocityId', reciprocityId);
    }
    if (situsState && situsState !== '') {
      queryParams.append('situsState', situsState);
    }
    queryParams.append('lang', language);
    const queryString = queryParams.toString();
    const basePath = `/guest-with-plan?${queryString}`;
    window.location.href = basePath;
  };

  const [, setLob] = useSessionStorage<string | undefined>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );

  const handleOnClick = () => {
    setLob(lob);
    if (portalData.portalName === 'umr') {
      return handleUmrOnClick();
    }
    if (lob === LOB.IFPEarlyPay) {
      return handleIfpEarlyPayOnClick();
    }

    if (isGuest && lob.includes('-guest')) {
      return handleGuestOnClick();
    }
    return handleObapiOnClick();
  };

  const parseBigNJson = () => {
    let bigNJson;
    try {
      bigNJson = JSON.parse(bigNJsonString);
    } catch {
      alert('Invalid JSON string');
    }

    if (isUserNonMaestroMNRMember) {
      return {
        id: bigNJson.id,
        userName: bigNJson.userName,
        portalSource: bigNJson.portalSource,
      };
    }

    if (isUserMNRMaestroMember) {
      return {
        firstName: bigNJson.firstName,
        lastName: bigNJson.lastName,
        dob: bigNJson.dob || bigNJson.dateOfBirth,
        memberId: bigNJson.memberId,
      };
    }

    return {
      firstName: bigNJson.firstName,
      lastName: bigNJson.lastName,
      dob: bigNJson.dob || bigNJson.dateOfBirth,
      groupNumber: bigNJson.groupNumber,
      memberId: bigNJson.memberId,
    };
  };

  const fillBigNJson = () => {
    const {
      firstName,
      lastName,
      groupNumber,
      memberId,
      dob,
      id,
      userName,
      portalSource,
    } = storage.session.get(DEMO_MEMBER_INFO) || {};

    if (isUserNonMaestroMNRMember) {
      form.setValue(
        'bigNJsonString',
        `{
          "id": "${id}",
          "userName": "${userName}",
          "portalSource": "${portalSource}"
        }`
      );
    } else {
      form.setValue(
        'bigNJsonString',
        `{
          "firstName": "${firstName}",
          "lastName": "${lastName}",
          "dob": "${dob}",
          "groupNumber": "${groupNumber}",
          "memberId": "${memberId}"
}`
      );
    }
  };

  const headerName = enablePreProd ? 'OBAPI - Pre Prod Flag Enabled' : 'OBAPI';

  const jsonPlaceholder = isUserNonMaestroMNRMember
    ? `{
    "id": "",
    "userName": "",
    "portalSource": ""
}`
    : `{
    "firstName": "",
    "lastName": "",
    "dob": "",
    "groupNumber": "",
    "memberId": ""
}`;

  // Mock Reciprocity ID Handlers
  const handleMockCoverageTypeChange = (e: string) => {
    setSelectedCoverageType(e);
  };

  const handleMockReciprocityIdChange = (id: string) => {
    setMockedReciprocityId(id);
  };

  useEffect(() => {
    if (isGuest && lob.includes('-guest')) {
      const parsedLOB = parseGuestLOB(lob as string).toUpperCase();
      const mappedPopulationOptions: string[] = Object.keys(
        reciprocityIdMapping[parsedLOB]
      );
      const defaultPopulation = mappedPopulationOptions[0];
      setPopulationOptions(mappedPopulationOptions);
      setPopulation(defaultPopulation);
    } else {
      setPopulationOptions([]);
      setPopulation('');
    }
  }, [lob]);

  // update membership category options when population or lob changes
  useEffect(() => {
    if (isGuest && lob.includes('-guest')) {
      const parsedLOB = parseGuestLOB(lob as string).toUpperCase();
      const mappedMembershipCategoryOptions: string[] =
        reciprocityIdMapping[parsedLOB][population];
      // check if the key M exists in object, if so, set to empty array
      if (Object.keys(mappedMembershipCategoryOptions || {}).includes('M')) {
        setMembershipCategoryOptions([]);
        setMembershipCategory('');
      } else {
        setMembershipCategoryOptions(
          Object.keys(mappedMembershipCategoryOptions || {})
        );
        setMembershipCategory(
          Object.keys(mappedMembershipCategoryOptions || {})[0] || ''
        );
      }
    }
  }, [population]);

  const handleCoverageTypeChange = (e) => {
    setCoverageType(e);
  };

  const handlePopulationChange = (e) => {
    setPopulation(e);
  };

  const handleMembershipCategoryChange = (e) => {
    setMembershipCategory(e);
  };

  // handle reciprocityId change
  const handleReciprocityIdChange = (e) => {
    setReciprocityId(e);
  };

  const handleSitusStateChange = (e: string) => {
    setSitusState(e);
  };

  // UPDATE reciprocityIdOptions when lob, guestCoverageType, population or membershipCategory changes
  // default to first option if exists
  useEffect(() => {
    if (isGuest && lob.includes('-guest')) {
      const parsedLOB = parseGuestLOB(lob as string).toUpperCase();
      let options = [];
      // skip if either population or membershipCategory is empty string or undefined
      if (!population || population === '') {
        setReciprociryIdOptions([]);
        setReciprocityId('');
        return;
      }
      try {
        if (membershipCategory && membershipCategory !== '') {
          options =
            reciprocityIdMapping[parsedLOB][population][membershipCategory][
              guestCoverageType ?? 'M'
            ];
        } else {
          options =
            reciprocityIdMapping[parsedLOB][population][
              guestCoverageType ?? 'M'
            ];
        }
      } catch {
        options = [];
      }
      setReciprociryIdOptions(options ?? []);
      if (options?.length > 0) {
        setReciprocityId(options[0]);
      } else {
        setReciprocityId('');
      }
    } else {
      setReciprociryIdOptions([]);
    }
  }, [guestCoverageType, population, membershipCategory]);

  return (
    <React.Fragment>
      <Heading color="$core.color.brand.100" level={6} size="sm">
        {headerName}
      </Heading>
      {!isGuest ? (
        <div style={{ width: '100%', padding: '6px 4px' }}>
          <Toggle
            css={{
              '& .abyss-toggle-label': {
                color: '$core.color.brand.100',
                fontWeight: 'bold',
              },
            }}
            data-testid="obapi-preprod-toggle"
            isChecked={enablePreProd}
            label="Pre Prod Toggle"
            onChange={(e: { target: { checked: any } }) =>
              setEnablePreProd(e.target.checked)
            }
          />
        </div>
      ) : null}
      {portalData?.portalName === 'umr' ? (
        <div style={{ width: '100%', padding: '6px 4px' }}>
          <Toggle
            css={{
              '& .abyss-toggle-label': {
                color: '$core.color.brand.100',
                fontWeight: 'bold',
              },
            }}
            data-testid="use-obapi-toggle"
            isChecked={useObapi}
            label="Use OBAPI"
            onChange={(e: { target: { checked: any } }) =>
              setUseObapi(e.target.checked)
            }
          />
          <Toggle
            css={{
              '& .abyss-toggle-label': {
                color: '$core.color.brand.100',
                fontWeight: 'bold',
              },
            }}
            data-testid="enable-pcp-toggle"
            isChecked={enablePcp}
            label="Enable PCP"
            onChange={(e: { target: { checked: any } }) =>
              setEnablePcp(e.target.checked)
            }
          />
        </div>
      ) : null}
      {/* Mock Reciprocity ID for QA Testing - Behavioral Coverage Provider Search */}
      <MockReciprocitySection>
        <MockReciprocitySectionTitle>
          QA Testing: Mock Reciprocity ID Override (Behavioral Coverage)
        </MockReciprocitySectionTitle>
        <div style={{ width: '100%', padding: '6px 4px' }}>
          <Toggle
            css={{
              'abyss-toggle-switch-label': {
                color: '$core.color.brand.100',
                fontWeight: 'bold',
              },
            }}
            data-testid="mock-reciprocity-toggle"
            isChecked={mockReciprocityConfig.isEnabled}
            label="Enable Mock Reciprocity ID"
            onChange={(e: { target: { checked: any } }) =>
              setMockReciprocityIsEnabled(e.target.checked)
            }
          />
        </div>
        {mockReciprocityConfig.isEnabled ? (
          <>
            <StyledSelectInput
              data-auto-testid="mock-reciprocity-coverage-type"
              data-testid="mock-reciprocity-coverage-type"
              label="Coverage Type to Mock"
              onChange={handleMockCoverageTypeChange}
              options={[
                { value: '', label: 'Select Coverage Type' },
                { value: 'M', label: 'Medical (M)' },
                { value: 'B', label: 'Behavioral (B)' },
                { value: 'D', label: 'Dental (D)' },
                { value: 'V', label: 'Vision (V)' },
              ]}
              placeholder="Select coverage type"
              value={mockReciprocityConfig.selectedCoverageType}
            />
            <StyledTextInput
              data-auto-testid="mock-reciprocity-id-input"
              data-testid="mock-reciprocity-id-input"
              label="Mocked Reciprocity ID"
              onChange={(e) => handleMockReciprocityIdChange(e.target.value)}
              placeholder="Enter reciprocity ID to use for selected coverage type"
              value={mockReciprocityConfig.mockedReciprocityId}
            />
            {mockReciprocityConfig.mockedReciprocityId &&
            mockReciprocityConfig.selectedCoverageType ? (
              <div
                style={{
                  color: '$core.color.green.100',
                  fontSize: '12px',
                  padding: '4px',
                  marginTop: '4px',
                }}
              >
                ✓ Mock active: When coverage type ={' '}
                {mockReciprocityConfig.selectedCoverageType}, reciprocity ID
                will use: {mockReciprocityConfig.mockedReciprocityId}
              </div>
            ) : null}
          </>
        ) : null}
      </MockReciprocitySection>
      {/* E&I */}
      {lob === LOB.ENI ||
      lob === LOB.CNS ||
      lob === LOB.IFP ||
      isUserMNRMaestroMember ? (
        <>
          <StyledTextInput
            data-auto-testid="obapi-firstName"
            data-testid="obapi-firstName"
            label="First Name"
            model="firstName"
            placeholder="First Name"
          />
          <StyledTextInput
            data-auto-testid="obapi-lastName"
            data-testid="obapi-lastName"
            label="Last Name"
            model="lastName"
            placeholder="Last Name"
          />
          <StyledTextInput
            data-auto-testid="obapi-dob"
            data-testid="obapi-dob"
            label="Date of birth (MM/DD/YYYY)"
            mask="##-##-####"
            model="dob"
            placeholder="Date of birth"
          />
          {!isUserMNRMaestroMember ? (
            <StyledTextInput
              data-auto-testid="obapi-groupNumber"
              data-testid="obapi-groupNumber"
              label="Group Number"
              model="groupNumber"
              placeholder="Group Number"
            />
          ) : null}
          <StyledTextInput
            data-auto-testid="obapi-memberId"
            data-testid="obapi-memberId"
            label="Member ID"
            model="memberId"
            placeholder="Member ID"
          />
          {isUserMNRMaestroMember ? (
            <StyledTextInput
              data-auto-testid="obapi-portalSource"
              data-testid="obapi-portalSource"
              label="Portal Source"
              model="portalSource"
              placeholder="Portal Source"
            />
          ) : null}
        </>
      ) : null}
      {portalData?.portalName === 'umr' ? (
        <>
          <StyledTextInput
            data-auto-testid="obapi-delsyscode"
            data-testid="obapi-delsyscode"
            label="Delsys Code"
            model="delsysCode"
            placeholder="Delsys Code"
          />
        </>
      ) : null}
      {/* MNR */}
      {isUserNonMaestroMNRMember ? (
        <>
          <StyledTextInput
            data-auto-testid="obapi-id"
            data-testid="obapi-id"
            label="ID"
            model="id"
            placeholder="ID"
          />
          <StyledTextInput
            data-auto-testid="obapi-userName"
            data-testid="obapi-userName"
            label="User Name"
            model="userName"
            placeholder="User Name"
          />
          <StyledTextInput
            data-auto-testid="obapi-portalSource"
            data-testid="obapi-portalSource"
            label="Portal Source"
            model="portalSource"
            placeholder="Portal Source"
          />
        </>
      ) : null}
      {lob === LOB.IFPEarlyPay ? (
        <>
          <StyledTextInput
            data-auto-testid="exchangeId"
            data-testid="exchangeId"
            label="Exchange ID"
            model="exchangeId"
            placeholder="Exchange ID"
          />
          <StyledTextInput
            data-auto-testid="ndbLob"
            data-testid="ndbLob"
            label="ndb Lob"
            model="ndbLob"
            placeholder="ndb Lob"
          />
          <StyledTextInput
            data-auto-testid="qhpId"
            data-testid="qhpId"
            label="QHP ID"
            model="qhpId"
            placeholder="QHP ID"
          />
          <StyledTextInput
            data-auto-testid="coverageEffectiveDate"
            data-testid="coverageEffectiveDate"
            label="Coverage Effective Date (MM/DD/YYYY)"
            mask="##-##-####"
            model="coverageEffectiveDate"
            placeholder="Coverage Effective Date"
          />
          <StyledTextInput
            data-auto-testid="stateCode"
            data-testid="stateCode"
            label="state Code"
            model="stateCode"
            placeholder="state Code"
          />
          <StyledTextInput
            data-auto-testid="zipCode"
            data-testid="zipCode"
            label="Zip Code"
            model="zipCode"
            placeholder="Zip Code"
          />
          <StyledTextInput
            data-auto-testid="language"
            data-testid="language"
            label="Language"
            model="language"
            placeholder="Language"
          />
          <StyledTextInput
            data-auto-testid="coverageType"
            data-testid="coverageType"
            label="Coverage Type"
            model="coverageType"
            placeholder="Coverage Type"
          />
          <StyledTextInput
            data-auto-testid="portalSource"
            data-testid="portalSource"
            label="Portal Source"
            model="portalSource"
            placeholder="Portal Source"
          />
        </>
      ) : null}
      {/** Guest */}
      {isGuest && lob !== LOB.IFPEarlyPay ? (
        <>
          <StyledSelectInput
            data-auto-testid="pick-coverage-type"
            data-testid="pick-coverage-type"
            label="Coverage Type"
            onChange={handleCoverageTypeChange}
            options={[
              { value: 'M', label: 'Medical' },
              { value: 'V', label: 'Vision' },
              { value: 'D', label: 'Dental' },
              { value: 'B', label: 'Behavioral' },
            ]}
            placeholder="Select a coverage type"
            value={guestCoverageType}
          />

          <StyledSelectInput
            data-auto-testid="pick-population"
            data-testid="pick-population"
            label="population"
            onChange={handlePopulationChange}
            options={populationOptions.map((pop) => ({
              value: pop,
              label: pop,
            }))}
            placeholder="Select a population"
            value={population}
          />
          <StyledSelectInput
            data-auto-testid="pick-membership-category"
            data-testid="pick-membership-category"
            isDisabled={membershipCategoryOptions.length === 0}
            label="Membership Category"
            onChange={handleMembershipCategoryChange}
            options={membershipCategoryOptions.map((mem) => ({
              value: mem,
              label: mem,
            }))}
            placeholder={
              membershipCategoryOptions.length > 0
                ? 'Select Membership Category'
                : 'N/A'
            }
            value={membershipCategory}
          />
          <StyledSelectInput
            data-auto-testid="pick-reciprocity-id"
            data-testid="pick-reciprocity-id"
            label="Reciprocity ID"
            onChange={handleReciprocityIdChange}
            options={reciprocityIdOptions.map((rec) => ({
              value: rec,
              label: rec,
            }))}
            placeholder="Select a reciprocity ID"
            value={reciprocityId}
          />
          <StyledTextInput
            data-auto-testid="situsState"
            data-testid="situsState"
            label="Situs State"
            model="situsState"
            onChange={(e) => handleSitusStateChange(e.target.value)}
            placeholder="Situs State"
            value={situsState}
          />
        </>
      ) : null}
      {lob && !portalData?.portalName ? (
        <React.Fragment>
          <StyledTextInputArea
            data-testid="obapi-bign-json"
            label="or, BigN JSON"
            model="bigNJsonString"
            placeholder={jsonPlaceholder}
          />
          <div style={{ width: '100%' }}>
            <Button
              css={{ marginTop: '$sm', marginBottom: '$md', display: 'block' }}
              data-testid="obapi-fill-bign-json"
              onClick={fillBigNJson}
              size="$sm"
            >
              Fill with current BigN
            </Button>
          </div>
        </React.Fragment>
      ) : null}
      <Button
        css={{ marginTop: '$sm', marginBottom: '$md' }}
        data-auto-testid="obapi-submit-button"
        data-testid="obapi-submit-button"
        onClick={handleOnClick}
        variant="outline"
      >
        Submit
      </Button>
    </React.Fragment>
  );
};
